import type { ReactNode } from 'react';

import { Skeleton } from '@flintmint/web-shared/components/skeleton';

/**
 * `lineHeight` is a text's scss line-height, `textHeight` its visible glyph height, and `charWidth` its
 * average Inter glyph width (measured), so a skeleton takes the text's line box and about its width.
 */
export type TextStyle = { lineHeight: number; textHeight: number; charWidth: number };

/** A `TextSkeleton` line takes an explicit width, so its style needs no glyph width. */
export type SkeletonLineStyle = Pick<TextStyle, 'lineHeight' | 'textHeight'>;

type TextProps = { children: ReactNode; style: TextStyle; loading: boolean };

/**
 * `children`, or while loading a skeleton sized from it in its place. Loading layouts pass fixed copy only;
 * a line that depends on data not loaded yet uses `TextSkeleton` with a measured width instead.
 */
export const Text = ({ children, style, loading }: TextProps) =>
  loading ? <TextSkeleton style={style} width={Math.round(String(children).length * style.charWidth)} /> : children;

/** A line whose text depends on data not loaded yet: its line box at an explicit, measured width. */
export const TextSkeleton = ({ style, width }: { style: SkeletonLineStyle; width: number | string }) => (
  <Skeleton trueHeight={style.lineHeight} height={style.textHeight} trueWidth={width} />
);
